import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { VehicleFormData, VehicleType, VehicleCategory } from '../../types';
import { vehicleService, vehicleSettingsService, vehicleNotesService } from '../../services/api';
import { FRENCH_LABELS, STATUS_OPTIONS } from '../../config/labels';

const FUEL_TYPES = ['Essence', 'Diesel', 'Électrique', 'Hybride'];
const TRANSMISSION_TYPES = ['Manuelle', 'Automatique'];

// Utility function to format date for HTML date input
const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toISOString().split('T')[0];
  } catch {
    return '';
  }
};

interface CreateVehicleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (vehicle: any) => void;
  initialPlateNumber?: string;
}

const CreateVehicleModal: React.FC<CreateVehicleModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialPlateNumber = '',
}) => {
  const [formData, setFormData] = useState<VehicleFormData>({
    registration_number: initialPlateNumber,
    marque: '',
    modele: '',
    year: new Date().getFullYear(),
    mileage: 0,
    status: 'ready',
    service_interval_km: 15000,
    // Initialize image fields
    primary_image: undefined,
    additional_images: undefined,
    remove_primary_image: false,
    remove_additional_images: [],
  });
  
  const [primaryImagePreview, setPrimaryImagePreview] = useState<string | null>(null);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [vehicleCategories, setVehicleCategories] = useState<VehicleCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Notes state for the notes section
  const [notes, setNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Load vehicle types and categories
  useEffect(() => {
    const loadVehicleTypesAndCategories = async () => {
      try {
        const settingsResponse = await vehicleSettingsService.getSettings();
        const settings = settingsResponse.data.data;
        
        // Get client-specific types and categories only
        setVehicleTypes(settings.types || []);
        setVehicleCategories(settings.categories || []);
      } catch (error) {
        console.error('Error loading vehicle types and categories:', error);
      }
    };

    if (open) {
      loadVehicleTypesAndCategories();
    }
  }, [open]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        registration_number: initialPlateNumber,
        marque: '',
        modele: '',
        year: new Date().getFullYear(),
        mileage: 0,
        status: 'ready',
        service_interval_km: 15000,
        vehicle_type_id: undefined,
        vehicle_category_id: undefined,
        // Initialize image fields for new vehicle
        primary_image: undefined,
        additional_images: undefined,
        remove_primary_image: false,
        remove_additional_images: [],
      });
      // Clear image previews for new vehicle
      setPrimaryImagePreview(null);
      setAdditionalImagePreviews([]);
      setError('');
    }
  }, [open, initialPlateNumber]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handlePrimaryImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide.');
        return;
      }
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert('La taille du fichier ne doit pas dépasser 2MB.');
        return;
      }
      
      setFormData(prev => ({ ...prev, primary_image: file }));
      const reader = new FileReader();
      reader.onload = () => setPrimaryImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };
  
  const handleAdditionalImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      // Validate files
      const validFiles = files.filter(file => {
        if (!file.type.startsWith('image/')) {
          alert(`Le fichier "${file.name}" n'est pas une image valide.`);
          return false;
        }
        if (file.size > 2 * 1024 * 1024) {
          alert(`Le fichier "${file.name}" est trop volumineux (max 2MB).`);
          return false;
        }
        return true;
      });
      
      if (validFiles.length > 0) {
        setFormData(prev => ({ 
          ...prev, 
          additional_images: [...(prev.additional_images || []), ...validFiles] 
        }));
        
        validFiles.forEach(file => {
          const reader = new FileReader();
          reader.onload = () => {
            setAdditionalImagePreviews(prev => [...prev, reader.result as string]);
          };
          reader.readAsDataURL(file);
        });
      }
    }
    // Reset input value to allow selecting the same files again
    event.target.value = '';
  };
  
  const removePrimaryImage = () => {
    setFormData(prev => ({ 
      ...prev, 
      primary_image: undefined, 
      remove_primary_image: true 
    }));
    // Clear preview safely
    setTimeout(() => {
      setPrimaryImagePreview(null);
    }, 0);
  };
  
  const removeAdditionalImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      additional_images: prev.additional_images?.filter((_, i) => i !== index)
    }));
    // Clear preview safely
    setTimeout(() => {
      setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await vehicleService.create(formData);
      onSuccess(response.data.data);
      onClose();
    } catch (err: any) {
      console.error('Error creating vehicle:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création du véhicule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' },
          height: { xs: '100vh', sm: 'auto' }
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CarIcon color="primary" />
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', sm: '1.25rem' }
          }}>
            Créer un nouveau véhicule
          </Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ 
          p: { xs: 1, sm: 2 },
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 1, sm: 2 }
          }
        }}>
          <Stack spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 1, sm: 2 } }}>
            {error && (
              <Alert severity="error" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {error}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={FRENCH_LABELS.registrationNumber}
                value={formData.registration_number}
                onChange={(e) => handleChange('registration_number', e.target.value.toUpperCase())}
                required
                variant="outlined"
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>{FRENCH_LABELS.status}</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  label={FRENCH_LABELS.status}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  {STATUS_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={FRENCH_LABELS.marque}
                value={formData.marque}
                onChange={(e) => handleChange('marque', e.target.value)}
                required
                variant="outlined"
                size="small"
              />
              <TextField
                fullWidth
                label={FRENCH_LABELS.modele}
                value={formData.modele}
                onChange={(e) => handleChange('modele', e.target.value)}
                required
                variant="outlined"
                size="small"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="number"
                label={FRENCH_LABELS.year}
                value={formData.year}
                onChange={(e) => handleChange('year', parseInt(e.target.value))}
                required
                variant="outlined"
                size="small"
                inputProps={{ min: 1990, max: new Date().getFullYear() + 1 }}
              />
              <TextField
                fullWidth
                type="number"
                label="Kilométrage"
                value={formData.mileage}
                onChange={(e) => handleChange('mileage', parseInt(e.target.value) || 0)}
                required
                variant="outlined"
                size="small"
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                type="number"
                label={FRENCH_LABELS.serviceInterval}
                value={formData.service_interval_km}
                onChange={(e) => handleChange('service_interval_km', parseInt(e.target.value) || 15000)}
                variant="outlined"
                size="small"
                inputProps={{ min: 0, step: 1000 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="date"
                label={FRENCH_LABELS.dateAchat}
                value={formData.date_achat || ''}
                onChange={(e) => handleChange('date_achat', e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label={FRENCH_LABELS.equipe}
                value={formData.equipe_assignee || ''}
                onChange={(e) => handleChange('equipe_assignee', e.target.value)}
                variant="outlined"
                size="small"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type de véhicule</InputLabel>
                <Select
                  value={formData.vehicle_type_id ? formData.vehicle_type_id.toString() : ''}
                  onChange={(e) => handleChange('vehicle_type_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  label="Type de véhicule"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Aucun</em>
                  </MenuItem>
                  {vehicleTypes.map(type => (
                    <MenuItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Catégorie de véhicule</InputLabel>
                <Select
                  value={formData.vehicle_category_id ? formData.vehicle_category_id.toString() : ''}
                  onChange={(e) => handleChange('vehicle_category_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  label="Catégorie de véhicule"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Aucune</em>
                  </MenuItem>
                  {vehicleCategories.map(category => (
                    <MenuItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{FRENCH_LABELS.fuelType}</InputLabel>
                <Select
                  value={formData.fuel_type || ''}
                  onChange={(e) => handleChange('fuel_type', e.target.value)}
                  label={FRENCH_LABELS.fuelType}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  {FUEL_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{FRENCH_LABELS.transmissionType}</InputLabel>
                <Select
                  value={formData.transmission_type || ''}
                  onChange={(e) => handleChange('transmission_type', e.target.value)}
                  label={FRENCH_LABELS.transmissionType}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  {TRANSMISSION_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={FRENCH_LABELS.color}
                value={formData.color || ''}
                onChange={(e) => handleChange('color', e.target.value)}
                variant="outlined"
                size="small"
              />
              <TextField
                fullWidth
                type="number"
                label="Nombre de portes"
                value={formData.nb_portes ?? ''}
                onChange={(e) => handleChange('nb_portes', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                variant="outlined"
                size="small"
                inputProps={{ min: 2, max: 6 }}
              />
              <TextField
                fullWidth
                type="number"
                label="Nombre de places"
                value={formData.nb_places ?? ''}
                onChange={(e) => handleChange('nb_places', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                variant="outlined"
                size="small"
                inputProps={{ min: 1, max: 20 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="date"
                label={FRENCH_LABELS.insuranceExpiry}
                value={formData.insurance_expiry || ''}
                onChange={(e) => handleChange('insurance_expiry', e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="date"
                label={FRENCH_LABELS.registrationExpiry}
                value={formData.first_registration_date || ''}
                onChange={(e) => handleChange('first_registration_date', e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            {/* Image Upload Section */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Images du véhicule
              </Typography>
              
              {/* Primary Image */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Image principale
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Cette image sera affichée en premier pour identifier le véhicule
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="primary-image-upload"
                    type="file"
                    onChange={handlePrimaryImageChange}
                  />
                  <label htmlFor="primary-image-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      size="small"
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      }}
                    >
                      Choisir une image
                    </Button>
                  </label>
                  {primaryImagePreview && (
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Avatar
                        src={primaryImagePreview}
                        sx={{ width: 80, height: 80 }}
                        variant="rounded"
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                        onClick={removePrimaryImage}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Stack>
              </Box>
              
              {/* Additional Images */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Images supplémentaires
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Ajoutez des photos supplémentaires du véhicule (max 2MB par image)
                </Typography>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="additional-images-upload"
                    type="file"
                    multiple
                    onChange={handleAdditionalImagesChange}
                  />
                  <label htmlFor="additional-images-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      size="small"
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      }}
                    >
                      Ajouter des images
                    </Button>
                  </label>
                  
                  {/* Image counter */}
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {additionalImagePreviews.length} image{additionalImagePreviews.length !== 1 ? 's' : ''}
                  </Typography>
                </Stack>
                
                {/* Image previews */}
                {additionalImagePreviews.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: 600 }}>
                    {additionalImagePreviews.map((preview, index) => (
                      <Box key={`preview-${index}`} sx={{ position: 'relative', display: 'inline-block' }}>
                        <Avatar
                          src={preview}
                          sx={{ width: 60, height: 60 }}
                          variant="rounded"
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' }
                          }}
                          onClick={() => removeAdditionalImage(index)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Notes Section */}
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" color="primary" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Notes
                </Typography>
                
                {/* Add Note Form */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Ajouter une note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    disabled={addingNote}
                    size="small"
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        // Note: For new vehicles, we can't add notes until after creation
                        // This is a limitation of the current implementation
                        alert('Les notes peuvent être ajoutées après la création du véhicule');
                      }}
                      disabled={!newNote.trim() || addingNote}
                      size="small"
                    >
                      {addingNote ? <CircularProgress size={16} /> : 'Ajouter'}
                    </Button>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Les notes peuvent être ajoutées après la création du véhicule
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1
        }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            fullWidth
            disabled={loading}
          >
            {FRENCH_LABELS.cancel}
          </Button>
          <Button 
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{ 
              minWidth: { xs: 'auto', sm: '120px' },
              py: { xs: 1.5, sm: 1 }
            }}
          >
            {loading ? <CircularProgress size={20} /> : FRENCH_LABELS.save}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateVehicleModal;